/**
 * Analytics utility functions - GTM handles GA4; these push events to dataLayer
 */

type EventParams = Record<string, string | number | boolean | null | undefined>;

/**
 * Safe gtag function that checks for availability (GTM exposes gtag via GA4 tag)
 */
const safeGtag = (command: string, ...args: unknown[]) => {
  if (typeof window !== 'undefined' && window.gtag) {
    (window.gtag as (...gtagArgs: unknown[]) => void)(command, ...args);
  }
};

/**
 * Track page views - pushes to dataLayer for GTM/GA4
 * @param path - Current path/route
 */
export const trackPageView = (path: string) => {
  safeGtag('event', 'page_view', {
    send_to: 'G-MXPE956L12',
    page_path: path,
    page_title: document.title,
    page_location: window.location.href,
    engagement_time_msec: performance.now()
  });
};

/**
 * Track button clicks
 * @param buttonName - Name/identifier of the button
 * @param category - Event category (default: 'engagement')
 * @param additionalParams - Any additional parameters to track
 */
export const trackButtonClick = (
  buttonName: string,
  category: string = 'engagement',
  additionalParams: EventParams = {}
) => {
  safeGtag('event', 'button_click', {
    event_category: category,
    event_label: buttonName,
    engagement_time_msec: performance.now(),
    ...additionalParams
  });
};

/**
 * Track outbound link clicks
 * @param url - Destination URL
 * @param linkText - Text of the link (if available)
 * @param additionalParams - Any additional parameters to track
 */
export const trackOutboundClick = (
  url: string,
  linkText: string = '',
  additionalParams: EventParams = {}
) => {
  safeGtag('event', 'outbound_click', {
    event_category: 'outbound',
    event_label: linkText || url,
    destination: url,
    engagement_time_msec: performance.now(),
    ...additionalParams
  });
};

/**
 * Track scroll depth
 * @param page - Current page/path
 * @param depth - Scroll depth percentage (default: 90)
 * @param additionalParams - Any additional parameters to track
 */
export const trackScrollDepth = (
  page: string,
  depth: number = 90,
  additionalParams: EventParams = {}
) => {
  safeGtag('event', 'scroll_depth', {
    event_category: 'engagement',
    event_label: page,
    value: depth,
    engagement_time_msec: performance.now(),
    ...additionalParams
  });
};

/**
 * Track affiliate link clicks
 * @param destination - Destination URL
 * @param platform - Platform name
 * @param position - Position on page (e.g., 'header', 'sidebar')
 */
export const trackAffiliateClick = (
  destination: string,
  platform: string,
  position: string = 'body'
) => {
  safeGtag('event', 'affiliate_click', {
    event_category: 'monetization',
    event_label: platform,
    destination: destination,
    position: position,
    engagement_time_msec: performance.now()
  });
};

/**
 * Load Ahrefs analytics script
 */
export const loadAhrefsAnalytics = async () => {
  if (typeof window === 'undefined' || document.getElementById('ahrefs-analytics-script')) {
    return;
  }

  try {
    const ahrefsScript = document.createElement('script');
    ahrefsScript.id = 'ahrefs-analytics-script';
    ahrefsScript.async = true;
    ahrefsScript.defer = true;
    ahrefsScript.src = 'https://analytics.ahrefs.com/analytics.js';
    ahrefsScript.setAttribute('data-key', 'gMw1FFp8wQZiPuzaMlzbMw');
    ahrefsScript.setAttribute('importance', 'low');

    document.head.appendChild(ahrefsScript);
  } catch (error) {
    console.error('Failed to load Ahrefs analytics:', error);
  }
};

/**
 * Initialize analytics (Ahrefs only; GTM loads from index.html)
 */
export const initAllAnalytics = async () => {
  setTimeout(() => {
    loadAhrefsAnalytics();
  }, 7000);
};
